import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, IconButton, FormGroup, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { ArrowBack, ArrowForwardIos } from '@mui/icons-material';
import moment from 'moment';
import { useIntl } from 'react-intl';
import messages from '../messages';

import { getStyles } from './styles';
import { getUserItemId, getUserName } from '../../../../utils/GetAccountDetails';
import { tostAlert } from '../../../../utils/AlertToast';
import { apiErrorHandler } from '../../../../utils/ApiErrorHandler';
import LoadBar from '../../../../utils/LoadBar';
import { useAppContext } from '../../../../context/AppContext';
import ExtractionIntegrationsApi from '../../../../Api/Services/AppflyteBackend/ExtractionIntegrationApi';
import { useDispatch } from 'react-redux';
import { setExtractionIntegrationAdded } from '../../../../Redux/slice/newDataSlice';

function AddIntegration({ setSelectedMenuItem }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();

    const connection_type = location?.state?.connection_type ?? null;
    const connection_label = location?.state?.connection_label ?? '';
    const action_type = location?.state?.action_type ?? '';
    const integration_data = location?.state?.integration_data ?? null;
    const { selectedWorkspace, selectedProject, selectedOrganization } = useAppContext();

    const [integrationName, setIntegrationName] = useState('')
    const webhookDetails = { http_method: 'post', url: '', auth_key: '' }
    const [webhookData, setWebhookData] = useState(webhookDetails)
    const s3_Details = { access_key: '', secret_access_key: '', endpoint_url: '' }
    const [s3Data, setS3Data] = useState(s3_Details)

    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (!connection_type) {
            navigate('/connectors/extraction-agent/connector-lists')
        }
    }, [connection_type])

    useEffect(() => {
        if (action_type === "put") {
            if (!integration_data) {
                navigate('/connectors/extraction-agent/connector-lists')
                return
            }
            getData()
        }
    }, [action_type])

    useEffect(() => {
        const errors = { ...formErrors };
        if (formErrors?.integrationName && integrationName?.trim()) { delete errors?.integrationName }

        if (formErrors?.url && webhookData?.url?.trim()) { delete errors?.url }
        if (formErrors?.auth_key && webhookData?.auth_key?.trim()) { delete errors?.auth_key }

        if (formErrors?.access_key && s3Data?.access_key?.trim()) { delete errors?.access_key }
        if (formErrors?.secret_access_key && s3Data?.secret_access_key?.trim()) { delete errors?.secret_access_key }
        if (formErrors?.endpoint_url && s3Data?.endpoint_url?.trim()) { delete errors?.endpoint_url }

        if (JSON.stringify(errors) !== JSON.stringify(formErrors)) { setFormErrors(errors) }
    }, [webhookData, integrationName, s3Data, formErrors]);

    const getData = async () => {
        setLoading(true)
        try {
            const integration_id = integration_data?.payload?.__auto_id__ ?? null;
            const response = await ExtractionIntegrationsApi.get_by_id(integration_id);
            if (response.status === 200) {
                const responseData = response.data;
                const payloadData = responseData?.payload ?? null;

                const name = payloadData?.name ?? '';
                const method = payloadData?.http_method?.toLowerCase();
                const http_method = method === 'put' ? 'put' : 'post';

                const url = payloadData?.url ?? '';
                const auth_key = payloadData?.auth_key ?? '';

                setWebhookData({
                    ...webhookData,
                    url: url,
                    auth_key: auth_key,
                    http_method: http_method
                })
                setIntegrationName(name)
            }
        } catch (error) {
            console.log('error')
            tostAlert(intl.formatMessage(messages.invalid_connector), 'warning')
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        setLoading(true)
        try {
            const errors = {};
            if (!integrationName?.trim()) { errors.integrationName = 'This Field is required' }

            if (connection_type === "webhook") {
                if (!webhookData.url?.trim()) { errors.url = 'This Field is required' }
                if (!webhookData.auth_key?.trim()) { errors.auth_key = 'This Field is required' }
            }

            if (connection_type === "aws_s3") {
                if (!s3Data.access_key?.trim()) { errors.access_key = 'This Field is required' }
                if (!s3Data.secret_access_key?.trim()) { errors.secret_access_key = 'This Field is required' }
                if (!s3Data.endpoint_url?.trim()) { errors.endpoint_url = 'This Field is required' }
            }

            if (Object.keys(errors).length === 0) {
                const userId = await getUserItemId();
                const reqObj = {
                    collection_item: {
                        name: integrationName,
                        enabled: true,
                        auth_required: true,
                        status: "connected",
                        auth_status: "valid",
                        http_method: webhookData.http_method,
                        url: webhookData.url,
                        auth_key: webhookData.auth_key,
                        access_key: s3Data.access_key,
                        secret_access_key: s3Data.secret_access_key,
                        endpoint_url: s3Data.endpoint_url,
                        type: connection_type,
                        organization_id: selectedOrganization?.payload?.__auto_id__ ?? null,
                        workspace_id: selectedWorkspace?.payload?.__auto_id__ ?? null,
                        project_id: selectedProject?.payload?.__auto_id__ ?? null,
                        created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                        created_by: userId
                    }
                };

                const collection_data = await ExtractionIntegrationsApi.checkNameExistence(integrationName)
                if (collection_data.data && collection_data.data.published_collections_detail.length > 0) {
                    const existing_data = collection_data.data.published_collections_detail.flatMap(collection => collection_data.data[collection.id]);
                    if (existing_data) {
                        const filtered_data = existing_data?.find(item => item?.payload?.project_id === selectedProject?.payload?.__auto_id__ && item?.payload?.name === integrationName) ?? null
                        if (filtered_data) {
                            tostAlert(intl.formatMessage(messages.connector_name_exist), 'warning')
                            return
                        }
                    }
                }

                const response = await ExtractionIntegrationsApi.addData(JSON.stringify(reqObj));
                if (response.status === 200) {
                    tostAlert(intl.formatMessage(messages.connector_success), 'success')
                    dispatch(setExtractionIntegrationAdded(true))
                    navigate('/connectors/extraction-agent/integrations')
                    setSelectedMenuItem(1)
                } else {
                    tostAlert(intl.formatMessage(messages.connector_error), 'error')
                }

            } else {
                setFormErrors(errors);
                return
            }
        } catch (error) {
            console.log(error)
            apiErrorHandler(error)
        } finally {
            setLoading(false)
        }
    }

    const handleUpdate = async () => {
        setLoading(true)
        try {
            const errors = {};
            if (!integrationName?.trim()) { errors.integrationName = 'This Field is required' }

            if (connection_type === "webhook") {
                if (!webhookData.url?.trim()) { errors.url = 'This Field is required' }
                if (!webhookData.auth_key?.trim()) { errors.auth_key = 'This Field is required' }
            }

            if (Object.keys(errors).length === 0) {

                const integration_id = integration_data?.payload?.__auto_id__;
                const update_key = integration_data?.update_key

                const reqObj = {
                    "id": integration_id,
                    "fields": [
                        { "path": "$.name", "value": integrationName },
                        { "path": "$.http_method", "value": webhookData.http_method },
                        { "path": "$.url", "value": webhookData.url },
                        { "path": "$.auth_key", "value": webhookData.auth_key },
                        { "path": "$.updated_on", "value": moment().format('YYYY-MM-DD HH:mm:ss') }
                    ]
                }

                const collection_data = await ExtractionIntegrationsApi.checkNameExistence(integrationName)
                if (collection_data.data && collection_data.data.published_collections_detail.length > 0) {
                    const existing_data = collection_data.data.published_collections_detail.flatMap(collection => collection_data.data[collection.id]);

                    if (existing_data?.length) {
                        const filtered_data = existing_data.find(item =>

                            item?.payload?.project_id === selectedProject?.payload?.__auto_id__ &&
                            item?.payload?.name === integrationName &&
                            item?.payload?.__auto_id__ !== integration_id
                        );
                        if (filtered_data) {
                            tostAlert(intl.formatMessage(messages.connector_name_exist), 'warning')
                            return;
                        }
                    }
                }

                const response = await ExtractionIntegrationsApi.update(JSON.stringify(reqObj), integration_id, update_key);
                if (response.status === 200) {
                    tostAlert(intl.formatMessage(messages.integration_update_success), 'success')
                    dispatch(setExtractionIntegrationAdded(true))
                    navigate('/connectors/extraction-agent/integrations')
                    setSelectedMenuItem(1)
                } else {
                    tostAlert(intl.formatMessage(messages.integration_update_error), 'error')
                }

            } else {
                setFormErrors(errors);
                return
            }
        } catch (error) {
            console.log(error)
            apiErrorHandler(error)
        } finally {
            setLoading(false)
        }
    }

    const connectorNames = {
        aws_s3: "AWS S3",
        webhook: "Webhook",
        azure_blob: "Azure Blob"
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                {loading && <LoadBar />}

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Box display={'flex'} alignItems={'center'}>
                            <IconButton onClick={() => navigate('/connectors/extraction-agent/connector-lists')}>
                                <ArrowBack sx={{ color: '#0B51C5' }} />
                            </IconButton>
                            <Typography
                                onClick={() => navigate('/connectors/extraction-agent/connector-lists')}
                                sx={{ ...styles.mainHeadingText, '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                            >
                                All Connectors
                            </Typography>
                            <ArrowForwardIos sx={{ fontSize: 16, ml: 1, mr: 1 }} />
                            <Typography sx={styles.mainHeadingText}>
                                {connectorNames[connection_label] || connection_label}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            onClick={() => action_type === "put" ? handleUpdate() : handleSave()}
                            disabled={loading}
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ ...styles.btnText, color: '#FFFFFF' }}>
                                {action_type === "put" ? "UPDATE" : "SAVE"}
                            </Typography>
                        </Button>
                        <Button
                            onClick={() => navigate('/connectors/extraction-agent/connector-lists')}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ ...styles.btnText, color: '#000000' }}>CANCEL</Typography>
                        </Button>
                    </Box>
                </Box>

                <Box mb={2} mt={2}>
                    <Typography mb={1}>Integration Name <span style={{ color: 'red' }}>*</span></Typography>
                    <TextField
                        variant="outlined"
                        type="text"
                        size='small'
                        sx={{ width: '600px' }}
                        value={integrationName}
                        onChange={(e) => setIntegrationName(e.target.value)}
                    />
                    {formErrors.integrationName && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.integrationName}</Typography>}
                </Box>

                {connection_type === "aws_s3" &&
                    (<Box mb={2}>
                        <FormGroup>
                            <Box mb={2}>
                                <Typography mb={1}>Access Key <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={s3Data.access_key}
                                    onChange={(e) => setS3Data({ ...s3Data, access_key: e.target.value })}
                                />
                                {formErrors.access_key && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.access_key}</Typography>}
                            </Box>

                            <Box mb={2}>
                                <Typography mb={1}>Secret Access Key <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={s3Data.secret_access_key}
                                    onChange={(e) => setS3Data({ ...s3Data, secret_access_key: e.target.value })}
                                />
                                {formErrors.secret_access_key && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.secret_access_key}</Typography>}
                            </Box>

                            <Box>
                                <Typography mb={1}>Endpoint URL <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={s3Data.endpoint_url}
                                    onChange={(e) => setS3Data({ ...s3Data, endpoint_url: e.target.value })}
                                />
                                {formErrors.endpoint_url && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.endpoint_url}</Typography>}
                            </Box>

                        </FormGroup>
                    </Box>)
                }

                {connection_type === "azure_blob" &&
                    (<Box>
                        {/* azure_blob */}
                    </Box>)
                }

                {connection_type === "webhook" &&
                    (<Box mt={2} mb={2}>
                        <FormGroup>

                            <Box mb={1}>
                                <Typography mb={0}>Http Method</Typography>
                                <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    style={{ display: 'flex', flexDirection: 'row' }}
                                    value={webhookData.http_method}
                                    onChange={(e) => setWebhookData({ ...webhookData, http_method: e.target.value })}
                                >
                                    <FormControlLabel value="post" control={<Radio sx={{ color: '#0B51C5' }} />} label="POST" />
                                    <FormControlLabel value="put" control={<Radio sx={{ color: '#0B51C5' }} />} label="PUT" />
                                </RadioGroup>
                            </Box>

                            <Box mb={2}>
                                <Typography mb={1}>URL <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={webhookData.url}
                                    onChange={(e) => setWebhookData({ ...webhookData, url: e.target.value })}
                                />
                                {formErrors.url && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.url}</Typography>}
                            </Box>

                            <Box>
                                <Typography mb={1}>Auth Key <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={webhookData.auth_key}
                                    onChange={(e) => setWebhookData({ ...webhookData, auth_key: e.target.value })}
                                />
                                {formErrors.auth_key && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.auth_key}</Typography>}
                            </Box>

                        </FormGroup>
                    </Box>)
                }
            </Box>
        </Box>
    )
}

export default AddIntegration;